import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '../CSS/JobUpload.css';
import '../CSS/DirectorySearch.css';

interface MonthlyNews {
  monthlyNewsId: number;
  monthlyNewsTitle: string;
  createdDate: string;
}

type SortField = 'monthlyNewsId' | 'monthlyNewsTitle' | 'createdDate';
type SortDirection = 'asc' | 'desc';

const MonthlyNewsDirectory: React.FC = () => {
  const navigate = useNavigate();
  const [monthlyNews, setMonthlyNews] = useState<MonthlyNews[]>([]);
  const [filteredMonthlyNews, setFilteredMonthlyNews] = useState<MonthlyNews[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [sortField, setSortField] = useState<SortField>('createdDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedMonthlyNews, setSelectedMonthlyNews] = useState<Set<number>>(new Set());
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const menuRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

  // Fetch monthly news from API
  useEffect(() => {
    const fetchMonthlyNews = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
        const response = await fetch('http://127.0.0.1:3060/api/admin/monthly-news', {
          headers: token ? {
            'Authorization': `Bearer ${token}`
          } : {}
        });
        const data = await response.json();

        if (data.success && data.data) {
          const monthlyNewsData: MonthlyNews[] = data.data.map((news: any) => ({
            monthlyNewsId: news.monthlyNewsId,
            monthlyNewsTitle: news.monthlyNewsTitle,
            createdDate: news.createdDate || news.lastUpdated || ''
          }));
          setMonthlyNews(monthlyNewsData);
          setFilteredMonthlyNews(monthlyNewsData);
        } else {
          setMonthlyNews([]);
          setFilteredMonthlyNews([]);
        }
      } catch (error) {
        console.error('Error fetching monthly news:', error);
        setMonthlyNews([]);
        setFilteredMonthlyNews([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMonthlyNews();
  }, []);

  // Filter monthly news based on search query
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredMonthlyNews(monthlyNews);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = monthlyNews.filter(news =>
        news.monthlyNewsId.toString().includes(query) ||
        news.monthlyNewsTitle.toLowerCase().includes(query)
      );
      setFilteredMonthlyNews(filtered);
    }
    setCurrentPage(1);
  }, [searchQuery, monthlyNews]);

  // Sort monthly news
  const sortedMonthlyNews = [...filteredMonthlyNews].sort((a, b) => {
    let aValue: any = a[sortField];
    let bValue: any = b[sortField];

    if (sortField === 'createdDate') {
      aValue = new Date(aValue || 0).getTime();
      bValue = new Date(bValue || 0).getTime();
    } else {
      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Pagination
  const totalPages = Math.ceil(sortedMonthlyNews.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedMonthlyNews = sortedMonthlyNews.slice(startIndex, startIndex + itemsPerPage);

  // Handle sort
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Handle checkbox selection
  const handleSelectMonthlyNews = (monthlyNewsId: number) => {
    const newSelected = new Set(selectedMonthlyNews);
    if (newSelected.has(monthlyNewsId)) {
      newSelected.delete(monthlyNewsId);
    } else {
      newSelected.add(monthlyNewsId);
    }
    setSelectedMonthlyNews(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedMonthlyNews.size === paginatedMonthlyNews.length) {
      setSelectedMonthlyNews(new Set());
    } else {
      setSelectedMonthlyNews(new Set(paginatedMonthlyNews.map(news => news.monthlyNewsId)));
    }
  };

  // Handle menu toggle
  const handleMenuToggle = (monthlyNewsId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    setOpenMenuId(openMenuId === monthlyNewsId ? null : monthlyNewsId);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openMenuId !== null) {
        const menuElement = menuRefs.current[openMenuId];
        if (menuElement && !menuElement.contains(event.target as Node)) {
          setOpenMenuId(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openMenuId]);

  // Handle edit monthly news
  const handleEditMonthlyNews = (monthlyNewsId: number) => {
    navigate(`/admin/edit-monthly-news?monthlyNewsId=${monthlyNewsId}`);
    setOpenMenuId(null);
  };

  // Handle delete monthly news
  const handleDeleteMonthlyNews = async (monthlyNewsId: number) => {
    if (!window.confirm(`Are you sure you want to delete newsletter ${monthlyNewsId}? This action cannot be undone.`)) {
      setOpenMenuId(null);
      return;
    }

    try {
      const token = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
      const response = await fetch(`http://127.0.0.1:3060/api/admin/monthly-news/${monthlyNewsId}`, {
        method: 'DELETE',
        headers: token ? {
          'Authorization': `Bearer ${token}`
        } : {}
      });

      if (response.ok) {
        // Remove monthly news from state
        setMonthlyNews(prev => prev.filter(news => news.monthlyNewsId !== monthlyNewsId));
        setSelectedMonthlyNews(prev => {
          const newSet = new Set(prev);
          newSet.delete(monthlyNewsId);
          return newSet;
        });
        alert('Newsletter deleted successfully');
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete newsletter');
      }
    } catch (error: any) {
      console.error('Error deleting newsletter:', error);
      alert(error.message || 'Error deleting newsletter. Please try again.');
    } finally {
      setOpenMenuId(null);
    }
  };

  // Export to CSV
  const handleExportCSV = () => {
    if (sortedMonthlyNews.length === 0) {
      alert('No newsletters to export');
      return;
    }

    const headers = ['ID', 'Newsletter Title', 'Created on'];
    const csvRows = [
      headers.join(','),
      ...sortedMonthlyNews.map(news => {
        const createdDate = news.createdDate 
          ? new Date(news.createdDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
          : '';
        const createdTime = news.createdDate
          ? new Date(news.createdDate).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
          : '';
        const createdOn = createdDate && createdTime ? `${createdDate} at ${createdTime}` : createdDate || '';
        
        return [
          news.monthlyNewsId,
          `"${news.monthlyNewsTitle.replace(/"/g, '""')}"`,
          `"${createdOn}"`
        ].join(',');
      })
    ];

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `newsletters-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Format date
  const formatDate = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  return (
    <div className="job-upload-page">
      <div className="job-upload-container" style={{ width: '90vw', maxWidth: 'none' }}>
        {/* Header with Search, Filter, and Action Buttons */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', gap: '1rem', flexWrap: 'wrap' }}>
          {/* Search Bar */}
          <div style={{ flex: '1', minWidth: '300px', position: 'relative' }}>
            <input
              type="text"
              placeholder="Search by ID or Newsletter Title"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem 1rem 0.75rem 2.5rem',
                border: '1px solid #d0d0d0',
                borderRadius: '8px',
                fontSize: '1rem',
                fontFamily: 'Lato, sans-serif'
              }}
            />
            <svg
              style={{
                position: 'absolute',
                left: '0.75rem',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '20px',
                height: '20px',
                pointerEvents: 'none'
              }}
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M19 19L13 13M15 8C15 11.866 11.866 15 8 15C4.13401 15 1 11.866 1 8C1 4.13401 4.13401 1 8 1C11.866 1 15 4.13401 15 8Z"
                stroke="#666666"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          {/* Filter Button */}
          <button
            type="button"
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#ffffff',
              color: '#000000',
              border: '1px solid #d0d0d0',
              borderRadius: '8px',
              fontSize: '1rem',
              fontFamily: 'Lato, sans-serif',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M2 4H14M4 8H12M6 12H10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Filter
          </button>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => navigate('/admin/upload-monthly-news')}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#0E4877',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontFamily: 'Lato, sans-serif',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              + New Newsletter
            </button>
            <button
              type="button"
              onClick={handleExportCSV}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#ffffff',
                color: '#000000',
                border: '1px solid #d0d0d0',
                borderRadius: '8px',
                fontSize: '1rem',
                fontFamily: 'Lato, sans-serif',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 2V11M8 11L5 8M8 11L11 8M2 13H14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Export as .CSV
            </button>
            <button
              type="button"
              onClick={() => navigate('/news-and-insights/monthly-newsletter?fromAdmin=true')}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#ffffff',
                color: '#000000',
                border: '1px solid #d0d0d0',
                borderRadius: '8px',
                fontSize: '1rem',
                fontFamily: 'Lato, sans-serif',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2ZM8 12C5.79086 12 4 10.2091 4 8C4 5.79086 5.79086 4 8 4C10.2091 4 12 5.79086 12 8C12 10.2091 10.2091 12 8 12Z" fill="currentColor"/>
                <path d="M8 5C6.34315 5 5 6.34315 5 8C5 9.65685 6.34315 11 8 11C9.65685 11 11 9.65685 11 8C11 6.34315 9.65685 5 8 5Z" fill="currentColor"/>
              </svg>
              View as User
            </button>
          </div>
        </div>

        {/* Table */}
        {isLoading ? (
          <div style={{ padding: '2rem', textAlign: 'left' }}>Loading newsletters...</div>
        ) : (
          <>
            <div style={{ border: '1px solid #d0d0d0', borderRadius: '8px', overflow: 'hidden', backgroundColor: '#ffffff' }}>
              {/* Table Header */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '40px 100px 2fr 200px 60px',
                backgroundColor: '#16416F',
                color: '#ffffff',
                fontFamily: 'Poppins, sans-serif',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}>
                <div style={{ padding: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <input
                    type="checkbox"
                    checked={selectedMonthlyNews.size === paginatedMonthlyNews.length && paginatedMonthlyNews.length > 0}
                    onChange={handleSelectAll}
                    style={{ cursor: 'pointer' }}
                  />
                </div>
                <div
                  style={{ padding: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', textAlign: 'left' }}
                  onClick={() => handleSort('monthlyNewsId')}
                >
                  ID
                  <span style={{ display: 'flex', flexDirection: 'column', fontSize: '0.7rem' }}>
                    <span style={{ opacity: sortField === 'monthlyNewsId' && sortDirection === 'asc' ? 1 : 0.3 }}>▲</span>
                    <span style={{ opacity: sortField === 'monthlyNewsId' && sortDirection === 'desc' ? 1 : 0.3 }}>▼</span>
                  </span>
                </div>
                <div
                  style={{ padding: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', textAlign: 'left' }}
                  onClick={() => handleSort('monthlyNewsTitle')}
                >
                  Newsletter Title
                  <span style={{ display: 'flex', flexDirection: 'column', fontSize: '0.7rem' }}>
                    <span style={{ opacity: sortField === 'monthlyNewsTitle' && sortDirection === 'asc' ? 1 : 0.3 }}>▲</span>
                    <span style={{ opacity: sortField === 'monthlyNewsTitle' && sortDirection === 'desc' ? 1 : 0.3 }}>▼</span>
                  </span>
                </div>
                <div
                  style={{ padding: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', textAlign: 'left' }}
                  onClick={() => handleSort('createdDate')}
                >
                  Created on
                  <span style={{ display: 'flex', flexDirection: 'column', fontSize: '0.7rem' }}>
                    <span style={{ opacity: sortField === 'createdDate' && sortDirection === 'asc' ? 1 : 0.3 }}>▲</span>
                    <span style={{ opacity: sortField === 'createdDate' && sortDirection === 'desc' ? 1 : 0.3 }}>▼</span>
                  </span>
                </div>
                <div style={{ padding: '1rem' }}></div>
              </div>

              {/* Table Rows */}
              {paginatedMonthlyNews.length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'left', color: '#666' }}>No newsletters found</div>
              ) : (
                paginatedMonthlyNews.map((news) => (
                  <div
                    key={news.monthlyNewsId}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '40px 100px 2fr 200px 60px',
                      borderTop: '1px solid #e0e0e0',
                      transition: 'background-color 0.2s',
                      position: 'relative'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f5f5f5';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#ffffff';
                    }}
                  >
                    <div style={{ padding: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <input
                        type="checkbox"
                        checked={selectedMonthlyNews.has(news.monthlyNewsId)}
                        onChange={() => handleSelectMonthlyNews(news.monthlyNewsId)}
                        style={{ cursor: 'pointer' }}
                      />
                    </div>
                    <div style={{ padding: '1rem', fontFamily: 'Lato, sans-serif', fontSize: '1rem', textAlign: 'left' }}>
                      {news.monthlyNewsId}
                    </div>
                    <div style={{ padding: '1rem', fontFamily: 'Lato, sans-serif', fontSize: '1rem', textAlign: 'left' }}>
                      {news.monthlyNewsTitle}
                    </div>
                    <div style={{ padding: '1rem', fontFamily: 'Lato, sans-serif', fontSize: '1rem', textAlign: 'left' }}>
                      {news.createdDate ? `${formatDate(news.createdDate)} at ${formatTime(news.createdDate)}` : ''}
                    </div>
                    <div style={{ padding: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                      <button
                        type="button"
                        onClick={(e) => handleMenuToggle(news.monthlyNewsId, e)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '0.25rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                          <circle cx="10" cy="5" r="1.5" fill="#666666"/>
                          <circle cx="10" cy="10" r="1.5" fill="#666666"/>
                          <circle cx="10" cy="15" r="1.5" fill="#666666"/>
                        </svg>
                      </button>
                      {openMenuId === news.monthlyNewsId && (
                        <div
                          ref={(el) => menuRefs.current[news.monthlyNewsId] = el}
                          style={{
                            position: 'absolute',
                            top: '0',
                            right: '100%',
                            marginRight: '0.5rem',
                            backgroundColor: '#ffffff',
                            border: '1px solid #d0d0d0',
                            borderRadius: '4px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                            zIndex: 1000,
                            minWidth: '150px'
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => handleEditMonthlyNews(news.monthlyNewsId)}
                            style={{
                              width: '100%',
                              padding: '0.75rem 1rem',
                              textAlign: 'left',
                              background: 'none',
                              border: 'none',
                              fontFamily: 'Lato, sans-serif',
                              fontSize: '0.9rem',
                              cursor: 'pointer',
                              borderBottom: '1px solid #e0e0e0'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#f5f5f5';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '#ffffff';
                            }}
                          >
                            Edit Newsletter
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteMonthlyNews(news.monthlyNewsId)}
                            style={{
                              width: '100%',
                              padding: '0.75rem 1rem',
                              textAlign: 'left',
                              background: 'none',
                              border: 'none',
                              fontFamily: 'Lato, sans-serif',
                              fontSize: '0.9rem',
                              cursor: 'pointer',
                              color: '#d32f2f'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#f5f5f5';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '#ffffff';
                            }}
                          >
                            Remove this Newsletter
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Pagination */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '1.5rem',
              padding: '1rem 0'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.9rem' }}>Items per page:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  style={{
                    padding: '0.5rem',
                    border: '1px solid #d0d0d0',
                    borderRadius: '4px',
                    fontFamily: 'Lato, sans-serif',
                    fontSize: '0.9rem',
                    cursor: 'pointer'
                  }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
              <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.9rem' }}>
                Page {currentPage} of {totalPages || 1}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  type="button"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  style={{
                    padding: '0.5rem',
                    background: 'none',
                    border: '1px solid #d0d0d0',
                    borderRadius: '4px',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    opacity: currentPage === 1 ? 0.5 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages || totalPages === 0}
                  style={{
                    padding: '0.5rem',
                    background: 'none',
                    border: '1px solid #d0d0d0',
                    borderRadius: '4px',
                    cursor: (currentPage === totalPages || totalPages === 0) ? 'not-allowed' : 'pointer',
                    opacity: (currentPage === totalPages || totalPages === 0) ? 0.5 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MonthlyNewsDirectory;

